/**
 * mock-exam-picker.ts
 *
 * Subject + level picker screen shown before entering the formal exam.
 * Rendered from main.ts when the player taps "模擬考" on the home screen.
 */

import type { Subject } from '../../shared/types';
import { mountEscapeButton } from '../escape-button';
import { runFormalExam } from './formal-exam';

interface MockExamPickerOpts {
  onBack: () => void;
}

interface SubjectOption {
  id: Subject;
  label: string;
  emoji: string;
}

const SUBJECTS: SubjectOption[] = [
  { id: 'math',    label: '數學 (星算門)',   emoji: '⚡' },
  { id: 'chinese', label: '國語 (符文門)',   emoji: '🔮' },
  { id: 'english', label: '英語 (機械門)',   emoji: '🤖' },
  { id: 'nature',  label: '自然 (自然門)',   emoji: '🌀' },
  { id: 'social',  label: '社會 (疆域門)',   emoji: '🏰' },
];

export function renderMockExamPicker(container: HTMLElement, opts: MockExamPickerOpts): void {
  const { onBack } = opts;

  const root = document.createElement('div');
  root.className = 'mock-exam-picker';
  container.appendChild(root);

  let selectedSubject: Subject | null = null;
  let selectedLevel: 'midterm' | 'final' | null = null;

  root.innerHTML = `
    <h1 class="mep-title">📝 模擬考</h1>
    <p class="mep-subtitle">選擇科目與考試類型，進入正式考卷模式</p>

    <div class="mep-section">
      <h3 class="mep-section-label">選擇科目</h3>
      <div class="mep-subject-grid" id="mep-subjects">
        ${SUBJECTS.map(s => `
          <button class="mep-subject-btn" data-id="${s.id}">
            <span class="mep-subject-emoji">${s.emoji}</span>
            <span class="mep-subject-label">${s.label}</span>
          </button>
        `).join('')}
      </div>
    </div>

    <div class="mep-section">
      <h3 class="mep-section-label">考試類型</h3>
      <div class="mep-level-row" id="mep-levels">
        <button class="mep-level-btn" data-level="midterm">
          <span class="mep-level-icon">📋</span>
          <span class="mep-level-name">期中考</span>
          <span class="mep-level-info">30 題 / 30 分鐘</span>
        </button>
        <button class="mep-level-btn" data-level="final">
          <span class="mep-level-icon">📄</span>
          <span class="mep-level-name">期末考</span>
          <span class="mep-level-info">50 題 / 50 分鐘</span>
        </button>
      </div>
    </div>

    <button class="primary-btn mep-start-btn" id="mep-start" disabled>開始作答</button>
  `;

  // Subject selection
  root.querySelectorAll<HTMLButtonElement>('.mep-subject-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      root.querySelectorAll('.mep-subject-btn').forEach(b => b.classList.remove('mep-selected'));
      btn.classList.add('mep-selected');
      selectedSubject = btn.dataset.id as Subject;
      updateStartBtn();
    });
  });

  // Level selection
  root.querySelectorAll<HTMLButtonElement>('.mep-level-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      root.querySelectorAll('.mep-level-btn').forEach(b => b.classList.remove('mep-selected'));
      btn.classList.add('mep-selected');
      selectedLevel = btn.dataset.level as 'midterm' | 'final';
      updateStartBtn();
    });
  });

  function updateStartBtn(): void {
    const startBtn = root.querySelector<HTMLButtonElement>('#mep-start')!;
    startBtn.disabled = !selectedSubject || !selectedLevel;
  }

  root.querySelector('#mep-start')!.addEventListener('click', () => {
    if (!selectedSubject || !selectedLevel) return;
    unmountEscape();
    root.remove();
    runFormalExam({
      container,
      subject: selectedSubject,
      level: selectedLevel,
      onDone: onBack,
    });
  });

  const unmountEscape = mountEscapeButton(() => {
    unmountEscape();
    root.remove();
    onBack();
  });
}
